import { TextAreaInput } from "@/components/shared/form/text-area-input";
import { TextInput } from "@/components/shared/form/text-input";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCreateAccount } from "@/queries/accounts/create";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { zodResolver } from "@hookform/resolvers/zod";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "../../ui/button";
import { Form } from "../../ui/form";
import { Shortcut } from "../../ui/shortcut";

type NewAccountFormProps = {
  onSuccess: (accountId: string) => void;
  initialValues?: {
    accountNo: string;
    accountName: string;
    accountDescription: string;
  };
};

export const NewAccountForm = ({
  onSuccess,
  initialValues = {
    accountNo: "",
    accountName: "",
    accountDescription: "",
  },
}: NewAccountFormProps) => {
  const { t } = useTranslation();
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const shortcutStyle = "bg-white/20 border border-white/30 text-white";

  const formSchema = z.object({
    accountNo: z
      .string()
      .min(2, { message: t("accounts.validation.account_number_min") })
      .max(50, { message: t("accounts.validation.account_number_max") }),
    accountName: z
      .string()
      .min(2, { message: t("accounts.validation.account_name_min") })
      .max(120, { message: t("accounts.validation.account_name_max") }),
    accountDescription: z
      .string()
      .max(600, { message: t("accounts.validation.account_description_max") })
      .optional(),
  });

  const { createAccount, loading } = useCreateAccount();
  const { refetch } = useGetAllAccounts();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        (e.metaKey || e.ctrlKey) &&
        submitButtonRef.current
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
        submitButtonRef.current.click();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const handleSubmit = ({
    accountName,
    accountNo,
    accountDescription,
  }: z.infer<typeof formSchema>) => {
    posthog.capture("new_account_created", {
      accountName,
      accountNo,
      accountDescription,
      url: window.location.href,
    });
    createAccount(
      { accountDescription, accountNo, accountName },
      (createdAccount) => {
        refetch();
        onSuccess(createdAccount.accountId);
      },
    );
  };

  const onSubmit = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    form.handleSubmit(handleSubmit)(e);
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && !(e.metaKey || e.ctrlKey)) {
      e.preventDefault();
    }
  };

  return (
    <Form {...form}>
      <form
        className="grid gap-4 py-4"
        onSubmit={onSubmit}
        onKeyDown={handleFormKeyDown}
      >
        <DialogHeader>
          <DialogTitle>{t("accounts.new_title")}</DialogTitle>
        </DialogHeader>
        <TextInput
          name="accountNo"
          label={t("account_number")}
          placeholder={t("accounts.number_placeholder")}
          autoFocus
        />
        <TextInput
          name="accountName"
          label={t("name")}
          placeholder={t("accounts.name_placeholder")}
        />
        <TextAreaInput
          name="accountDescription"
          label={t("accounts.description.label")}
          placeholder={t("accounts.description.placeholder")}
          description={t("accounts.description.helper")}
        />
        <DialogFooter>
          <Button
            ref={submitButtonRef}
            disabled={loading}
            type="submit"
            className="gap-3"
          >
            {t("accounts.save")}
            <Shortcut.ShortcutRoot>
              {isMac ? (
                <Shortcut.ShortcutCommand size="sm" className={shortcutStyle} />
              ) : (
                <Shortcut.ShortcutHotKey size="sm" className={shortcutStyle}>
                  Ctrl
                </Shortcut.ShortcutHotKey>
              )}
              <Shortcut.ShortcutEnter size="sm" className={shortcutStyle} />
            </Shortcut.ShortcutRoot>
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
